// Several api actions refuse with a specific, actionable reason in the
// response body: "A valid, unexpired License Key is required to generate a
// Resource Key." from ResourceKeyController.NewResource (issue #121
// defect 1), or the properties to drop from the registration refusals of
// issues #75 and #76. That reason decides what the customer does next, so
// it must reach the page rather than being replaced by one generic
// sentence that blames a key which may be structurally fine.
//
// Reading it lives here, once, rather than at each call site. register()
// had already grown its own version that stripped the JSON quotes with a
// regular expression and did nothing else, so the same body arriving at
// two callers was read two different ways.

// A stray HTML error page or an oversized payload must not be able to fill
// the UI, so anything longer than this is cut down with an ellipsis.
const MAX_MESSAGE_LENGTH = 300;

// The properties a refusal's reason arrives in, most specific first.
// 'message' is what the configurator's own responses use. 'detail' and
// 'title' are ProblemDetails, which is what ASP.NET Core produces for an
// [ApiController]'s automatic 400 and for a handled 500; without them a
// customer would be shown the raw ProblemDetails JSON, traceId and all.
const MESSAGE_PROPERTIES = ['message', 'detail', 'title'];

function truncate(text) {
    if (text.length <= MAX_MESSAGE_LENGTH) {
        return text;
    }
    return text.slice(0, MAX_MESSAGE_LENGTH) + '...';
}

function looksLikeHtml(text) {
    return text.startsWith('<');
}

// The reason carried by a parsed JSON object, or undefined when it carries
// none. An object with no recognised property is not a message meant for a
// customer, so the caller's default is shown instead of its JSON.
function messageFromObject(parsed) {
    for (const property of MESSAGE_PROPERTIES) {
        if (typeof parsed[property] === 'string') {
            return parsed[property];
        }
    }
    return undefined;
}

// The server's own reason for refusing a request, read from the response
// body, or defaultMessage when there is nothing usable to show: an empty
// body, a body that could not be read, or something that looks like an
// HTML error page rather than a message meant for the customer.
//
// ResourceKeyController and ConfigurationController both carry
// [Produces("application/json")] and return their refusals as
// StatusCode(403, "some plain string"), which ASP.NET Core's default JSON
// output formatter writes as a quoted JSON string, not as unquoted plain
// text. Reading the body with response.text() first and only then trying
// JSON.parse handles that bare-string shape as well as a JSON object
// carrying a reason, so this is the robust order rather than assuming
// either shape up front.
export async function serverErrorMessage(response, defaultMessage) {
    let text;
    try {
        text = await response.text();
    } catch (exception) {
        return defaultMessage;
    }

    if (Boolean(text) === false) {
        return defaultMessage;
    }

    let candidate = text;
    try {
        const parsed = JSON.parse(text);
        if (typeof parsed === 'string') {
            candidate = parsed;
        } else if (parsed !== null && typeof parsed === 'object') {
            const fromObject = messageFromObject(parsed);
            if (fromObject === undefined) {
                return defaultMessage;
            }
            candidate = fromObject;
        }
    } catch (exception) {
        // Not JSON, so the raw text is the best candidate as it stands.
    }

    candidate = candidate.trim();
    if (candidate === '' || looksLikeHtml(candidate) === true) {
        return defaultMessage;
    }

    return truncate(candidate);
}
